import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { auth, onAuthStateChanged } from '../../../../auth';
import convex, { waitForConvexAuthReady } from '../../../../convexClient';
import { api } from '../../../../convex/_generated/api';
import { cleanForFirestore, getMonthDateRange } from '../../../../utils/helperFunctions';
import useAccountStore from '../../Accounts/Store/useAccountStore';
import { LONG_CACHE_TTL, DAY_TTL } from '../../../../utils/cacheConfig';
import { withActiveTenant } from '../../../../utils/tenantUtils';
import { computeMonthlyTotals } from '../utils/monthlyTotals';

const CACHE_EXPIRATION_TIME = LONG_CACHE_TTL;
const INFLIGHT_ALL_BY_ACCOUNT = new Map();
const INFLIGHT_BY_ACCOUNT_KEY = new Map();
const INFLIGHT_BY_GOAL = new Map();

const normalizeTransaction = (tx) => ({
  ...tx,
  id: tx?.id || tx?._id,
});

const normalizeTransactions = (items = []) => items.map(normalizeTransaction);
const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

let authReadyPromise = null;
function ensureUser() {
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  if (!authReadyPromise) {
    authReadyPromise = new Promise((resolve) => {
      const unsub = onAuthStateChanged(auth, (u) => { unsub(); resolve(u || null); });
    });
  }
  return authReadyPromise;
}

const useTransactionStore = create(
  persist(
    (set, get) => ({
      transactions: [],
      workspaceSuggestions: [],
      lastFetchedWorkspaceSuggestions: null,
      loading: false,
      error: null,
      lastFetchedTransactions: null,
      cachedTransactions: {},
      hasRecalculatedBalances: false,
      lastRecalculatedAt: null,
      openingBalanceByAccount: {},
      allTransactionsByAccount: {},
      lastFetchedByAccountKey: {},
      lastFetchedAllByAccount: {},

      setTransactions: (transactions) => set({ transactions, lastFetchedTransactions: Date.now() }),
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
      invalidateTransactionCaches: () =>
        set({
          lastFetchedTransactions: null,
          cachedTransactions: {},
          openingBalanceByAccount: {},
          allTransactionsByAccount: {},
          lastFetchedByAccountKey: {},
          lastFetchedAllByAccount: {},
        }),

      fetchAllByAccountId: async (accountId) => {
        const user = await ensureUser();
        if (!user || !accountId) return [];

        const lastFetchedAll = get().lastFetchedAllByAccount?.[accountId];
        const cachedAll = get().allTransactionsByAccount?.[accountId];
        if (lastFetchedAll && cachedAll && Date.now() - lastFetchedAll < CACHE_EXPIRATION_TIME) {
          return cachedAll;
        }

        if (INFLIGHT_ALL_BY_ACCOUNT.has(accountId)) {
          return INFLIGHT_ALL_BY_ACCOUNT.get(accountId);
        }

        const inflightPromise = (async () => {
          await waitForConvexAuthReady();
          const all = normalizeTransactions(await convex.query(
            api.transactions.listAllByAccount,
            withActiveTenant({ accountId })
          ));
          set({
            allTransactionsByAccount: { ...get().allTransactionsByAccount, [accountId]: all },
            lastFetchedAllByAccount: { ...(get().lastFetchedAllByAccount || {}), [accountId]: Date.now() },
          });
          return all;
        })();

        INFLIGHT_ALL_BY_ACCOUNT.set(accountId, inflightPromise);
        try { return await inflightPromise; }
        finally { INFLIGHT_ALL_BY_ACCOUNT.delete(accountId); }
      },

      fetchWorkspaceSuggestions: async () => {
        const lastFetched = get().lastFetchedWorkspaceSuggestions;
        if (lastFetched && Date.now() - lastFetched < CACHE_EXPIRATION_TIME) return;

        const user = await ensureUser();
        if (!user) return;

        const tenant = withActiveTenant();
        if (!tenant?.tenantId) return;

        try {
          await waitForConvexAuthReady();
          const suggestions = normalizeTransactions(
            await convex.query(api.transactions.listDescriptionsForWorkspace, tenant)
          );
          set({ workspaceSuggestions: suggestions, lastFetchedWorkspaceSuggestions: Date.now() });
        } catch (err) {
          // Non-fatal — fall back to user-scoped suggestions silently
        }
      },

      fetchAllTransactions: async () => {
        const lastFetchedTime = get().lastFetchedTransactions;
        const hasData = (get().transactions || []).length > 0;
        if (hasData && lastFetchedTime && Date.now() - lastFetchedTime < CACHE_EXPIRATION_TIME) return;

        set({ loading: true, error: null });
        const user = await ensureUser();
        if (!user) { set({ transactions: [], loading: false }); return; }

        try {
          await waitForConvexAuthReady();
          const transactions = normalizeTransactions(
            await convex.query(api.transactions.listByUser, withActiveTenant())
          );
          set({ transactions, lastFetchedTransactions: Date.now() });
        } catch (err) {
          set({ error: err.message });
        } finally {
          set({ loading: false });
        }
      },

      saveTransactionToFirestore: async (tx) => {
        const user = await ensureUser();
        if (!user) throw new Error("User not authenticated");

        const isoDate = (tx.date instanceof Date ? tx.date : new Date(tx.date)).toISOString();

        // When a loanId is present this is a loan repayment — route it through
        // the dedicated mutation so the loan balance, paired transaction, and
        // loanRepayments row are written atomically. Going through
        // transactions.createTransaction would silently drop the loan link.
        if (tx.loanId) {
          const repaymentPayload = cleanForFirestore({
            loanId: tx.loanId,
            amount: Number(tx.amount || 0),
            date: isoDate,
            // `fromAccountId` is interpreted per loan direction by the backend:
            // the debited account for a borrowed-loan repayment (expense), the
            // credited deposit account for a lent-loan payment received (income).
            // Income transactions carry the account in creditAccountId, so fall
            // back to it when there is no debit account.
            fromAccountId: tx.debitAccountId || tx.creditAccountId || undefined,
            notes: tx.description || undefined,
            principalPortion:
              tx.principalPortion != null ? Number(tx.principalPortion) : undefined,
            interestPortion:
              tx.interestPortion != null ? Number(tx.interestPortion) : undefined,
          });
          const result = await convex.mutation(
            api.loanRepayments.create,
            repaymentPayload
          );
          get().invalidateTransactionCaches();
          return result?.transactionId || result?.id || null;
        }

        const payload = cleanForFirestore({
          type: tx.type,
          flow: tx.flow || undefined,
          creditAccountId: tx.creditAccountId || undefined,
          debitAccountId: tx.debitAccountId || undefined,
          budgetId: tx.budgetId || undefined,
          budgetPeriod: tx.budgetPeriod || undefined,
          goalId: tx.goalId || undefined,
          loanId: tx.loanId || undefined,
          categoryId: tx.categoryId || undefined,
          description: tx.description || undefined,
          amount: Number(tx.amount || 0),
          date: isoDate,
        });

        const id = await convex.mutation(api.transactions.createTransaction, withActiveTenant(payload));
        get().invalidateTransactionCaches();
        return id;
      },

      updateTransactionInFirestore: async (id, tx) => {
        const user = await ensureUser();
        if (!user) throw new Error("User not authenticated");

        const isoDate = (tx.date instanceof Date ? tx.date : new Date(tx.date)).toISOString();

        // Loan-linked edits flow through loanRepayments.update so both the
        // repayment row and paired transaction stay consistent. Caller is
        // responsible for passing `repaymentId` when the loan-edit context is
        // available; otherwise fall back to a plain transaction update which
        // the backend now restricts to metadata fields when loanId is set.
        if (tx.loanId && tx.repaymentId) {
          const patch = cleanForFirestore({
            amount: tx.amount != null ? Number(tx.amount) : undefined,
            date: isoDate,
            // Interpreted per direction (see create path). Income payments
            // received carry the account in creditAccountId.
            fromAccountId: tx.debitAccountId || tx.creditAccountId || undefined,
            notes: tx.description || undefined,
            principalPortion:
              tx.principalPortion != null ? Number(tx.principalPortion) : undefined,
            interestPortion:
              tx.interestPortion != null ? Number(tx.interestPortion) : undefined,
          });
          await convex.mutation(api.loanRepayments.update, {
            id: tx.repaymentId,
            patch,
          });
          get().invalidateTransactionCaches();
          return true;
        }

        const payload = cleanForFirestore({
          id,
          type: tx.type,
          flow: tx.flow || undefined,
          creditAccountId: tx.creditAccountId || undefined,
          debitAccountId: tx.debitAccountId || undefined,
          budgetId: tx.budgetId || undefined,
          budgetPeriod: tx.budgetPeriod || undefined,
          goalId: tx.goalId || undefined,
          loanId: tx.loanId || undefined,
          categoryId: tx.categoryId || undefined,
          description: tx.description || undefined,
          amount: Number(tx.amount || 0),
          date: isoDate,
        });

        await convex.mutation(api.transactions.updateTransaction, withActiveTenant(payload));
        get().invalidateTransactionCaches();
        return true;
      },

      selectByBudget: (budgetId, budgetPeriod) => {
        const txs = get().transactions || [];
        return txs.filter(
          (t) => t.budgetId === budgetId && (budgetPeriod ? t.budgetPeriod === budgetPeriod : true)
        );
      },

      fetchTransactionsByBudgetId: async (budgetId, budgetPeriod) => {
        const user = await ensureUser();
        if (!user) { set({ error: "No authenticated user" }); return; }
        if (!budgetId) { set({ error: "Missing budgetId" }); return; }

        set({ loading: true });
        try {
          const transactions = normalizeTransactions(await convex.query(api.transactions.listByBudget, {
            budgetId,
            budgetPeriod: budgetPeriod || undefined,
            ...withActiveTenant(),
          }));
          set({ transactions });
        } catch (err) {
          set({ error: err.message });
        } finally {
          set({ loading: false });
        }
      },

      fetchByGoalId: async (goalId) => {
        const user = await ensureUser();
        if (!user) { set({ error: "No authenticated user" }); return; }
        if (!goalId) { set({ error: "Missing goalId" }); return; }

        const cacheKey = `goal:${goalId}`;
        const cached = get().cachedTransactions?.[cacheKey];
        if (cached && Date.now() - cached.fetchedAt < CACHE_EXPIRATION_TIME) {
          set({ transactions: cached.items });
          return cached.items;
        }

        if (INFLIGHT_BY_GOAL.has(goalId)) return INFLIGHT_BY_GOAL.get(goalId);

        const inflightPromise = (async () => {
          set({ loading: true });
          try {
            const merged = normalizeTransactions(
              await convex.query(api.transactions.listByGoal, withActiveTenant({ goalId }))
            );
            set({
              transactions: merged,
              cachedTransactions: {
                ...(get().cachedTransactions || {}),
                [cacheKey]: { items: merged, fetchedAt: Date.now() },
              },
            });
            return merged;
          } catch (err) {
            set({ error: err.message });
          } finally {
            set({ loading: false });
          }
        })();

        INFLIGHT_BY_GOAL.set(goalId, inflightPromise);
        try { return await inflightPromise; }
        finally { INFLIGHT_BY_GOAL.delete(goalId); }
      },

      fetchByAccountId: async (accountId, year, month) => {
        const user = await ensureUser();
        if (!user) { set({ error: "No authenticated user" }); return; }
        if (!accountId) { set({ error: "Missing accountId" }); return; }

        const cacheKey = `${accountId}|${year}|${month ?? "all"}`;
        const cached = get().cachedTransactions?.[cacheKey];
        if (cached && Date.now() - cached.fetchedAt < CACHE_EXPIRATION_TIME) {
          set({ transactions: cached.items });
          if (cached.openingBalance != null) {
            const periodKey = month ? `${year}-${String(month).padStart(2, "0")}` : String(year);
            set({
              openingBalanceByAccount: {
                ...(get().openingBalanceByAccount || {}),
                [accountId]: { ...(get().openingBalanceByAccount?.[accountId] || {}), [periodKey]: cached.openingBalance },
              },
            });
          }
          return cached.items;
        }

        if (INFLIGHT_BY_ACCOUNT_KEY.has(cacheKey)) return INFLIGHT_BY_ACCOUNT_KEY.get(cacheKey);

        const inflightPromise = (async () => {
          set({ loading: true });
          try {
            const range = month
              ? getMonthDateRange(year, month)
              : { startDate: new Date(year, 0, 1), endDate: new Date(year, 11, 31, 23, 59, 59, 999) };

            const merged = normalizeTransactions(await convex.query(api.transactions.listByAccount, {
              accountId,
              startDate: range?.startDate?.toISOString(),
              endDate: range?.endDate?.toISOString(),
              ...withActiveTenant(),
            }));

            if (!get().allTransactionsByAccount?.[accountId]) {
              await get().fetchAllByAccountId(accountId);
            }

            const openingCutoff = month ? new Date(year, month - 1, 1) : new Date(year, 0, 1);
            const openingBalance = get().getOpeningBalanceByAccount(accountId, openingCutoff);
            const periodKey = month ? `${year}-${String(month).padStart(2, "0")}` : String(year);

            set({
              transactions: merged,
              openingBalanceByAccount: {
                ...(get().openingBalanceByAccount || {}),
                [accountId]: { ...(get().openingBalanceByAccount?.[accountId] || {}), [periodKey]: openingBalance },
              },
              cachedTransactions: {
                ...(get().cachedTransactions || {}),
                [cacheKey]: { items: merged, fetchedAt: Date.now(), openingBalance },
              },
              lastFetchedByAccountKey: { ...(get().lastFetchedByAccountKey || {}), [cacheKey]: Date.now() },
            });
          } catch (err) {
            set({ error: err.message });
          } finally {
            set({ loading: false });
          }
          return get().transactions;
        })();

        INFLIGHT_BY_ACCOUNT_KEY.set(cacheKey, inflightPromise);
        try { return await inflightPromise; }
        finally { INFLIGHT_BY_ACCOUNT_KEY.delete(cacheKey); }
      },

      deleteTransaction: async (transactionId) => {
        const user = await ensureUser();
        if (!user) { set({ error: "No authenticated user" }); return false; }
        if (!transactionId) { set({ error: "Missing transactionId" }); return false; }

        set({ loading: true, error: null });
        try {
          await convex.mutation(api.transactions.deleteTransaction, { id: transactionId });
          get().invalidateTransactionCaches();
          return true;
        } catch (err) {
          set({ error: err.message });
          return false;
        } finally {
          set({ loading: false });
        }
      },

      getMonthlyTotals: (txs, accountId, type) =>
        computeMonthlyTotals(txs, accountId, type),

      getOpeningBalanceByAccount: (accountId, beforeDate) => {
        const txs = get().allTransactionsByAccount?.[accountId] || [];
        const account = useAccountStore
          .getState()
          .accounts?.find((item) => item.id === accountId || item._id === accountId);
        const netAcrossAllTransactions = txs.reduce((sum, tx) => {
          const amount = toNumber(tx.amount);
          let nextSum = sum;
          if (tx.creditAccountId === accountId) nextSum += amount;
          if (tx.debitAccountId === accountId) nextSum -= amount;
          return nextSum;
        }, 0);

        const baseOpeningBalance =
          account?.openingBalance !== undefined && account?.openingBalance !== null
            ? toNumber(account.openingBalance)
            : toNumber(account?.balance) - netAcrossAllTransactions;

        let balance = baseOpeningBalance;
        txs.forEach((tx) => {
          if (!tx.date) return;
          if (new Date(tx.date) >= beforeDate) return;
          const amount = toNumber(tx.amount);
          if (tx.creditAccountId === accountId) balance += amount;
          if (tx.debitAccountId === accountId) balance -= amount;
        });
        return balance;
      },

      recalculateAllAccountBalancesFromFirestore: async () => {
        const user = await ensureUser();
        if (!user) {
          return { status: "error", message: "No authenticated user." };
        }

        const lastRun = get().lastRecalculatedAt;
        if (lastRun && Date.now() - lastRun < DAY_TTL) {
          return { status: "throttled", message: "Already reconciled recently — try again later." };
        }

        set({ loading: true, error: null });
        try {
          const { updatedCount, skippedCount } = await convex.mutation(
            api.transactions.recalculateBalances,
            withActiveTenant()
          );

          // Refresh accounts in store
          await waitForConvexAuthReady();
          const updatedAccounts = await convex.query(api.accounts.listAccounts, withActiveTenant());
          useAccountStore.setState({ accounts: updatedAccounts });

          set({ hasRecalculatedBalances: true, lastRecalculatedAt: Date.now() });
          return { status: "success", updatedCount, skippedCount };
        } catch (err) {
          console.error("Balance recalculation failed:", err);
          return { status: "error", message: err.message };
        } finally {
          set({ loading: false });
        }
      },
    }),
    {
      name: 'transaction-store',
      partialize: (state) => ({
        transactions: state.transactions,
        lastFetchedTransactions: state.lastFetchedTransactions,
        cachedTransactions: state.cachedTransactions,
        hasRecalculatedBalances: state.hasRecalculatedBalances,
        lastRecalculatedAt: state.lastRecalculatedAt,
        openingBalanceByAccount: state.openingBalanceByAccount,
        allTransactionsByAccount: state.allTransactionsByAccount,
        lastFetchedByAccountKey: state.lastFetchedByAccountKey,
        lastFetchedAllByAccount: state.lastFetchedAllByAccount,
      }),
    }
  )
);

export default useTransactionStore;
